import React, { useMemo, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import CustomModal from './CustomModal';
import Image from './Image';
import Coin from '../assets/promotion/coin.png';
import Available from '../assets/promotion/available.png';
import Claimed from '../assets/promotion/claimed.png';
import DailyBackground from '../assets/promotion/dialyBackground.png';
import palette from '../theme/palette';
import useLocales from '../hooks/useLocales';
import { useDispatch, useSelector } from '../redux/store';
import { dailyCheckInWeekRewards } from '../config';
import conObj from '../utils/connection-assistant';
import { isSafariOrIOS } from '../utils/isSafariOrIOS';
import { getUserMemberBalance, setDailyCheckin } from '../redux/slices/lookup';

// =====================
// Helpers
// =====================
// const getDayIcon = (status) => {
//   if (status === 'claimed') {
//     return <Iconify icon="eva:checkmark-fill" sx={{ color: '#fff', fontSize: 22 }} />;
//   }
//   if (status === 'missed') {
//     return <Iconify icon="mdi:gift-off-outline" sx={{ color: '#fff', fontSize: 22 }} />;
//   }
//   return <Iconify icon="mdi:gift-outline" sx={{ color: '#f2c556', fontSize: 22 }} />;
// };

const getDayIcon = (status) => {
  if (status === 'claimed') {
    // return <Image src={Claimed} alt="claimed" sx={{ width: 32, height: 32, filter: 'drop-shadow(0px 4px 6px rgba(0, 200, 0, 0.6))'}} />;
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center' }}>
        <Image src={Claimed} alt="claimed" sx={{ width: 45, height: 32 }} />
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 24,
            height: 6,
            borderRadius: '50%',
            background: 'rgba(0, 180, 0, 0.35)',
            filter: 'blur(3px)',
          }}
        />
      </Box>
    );
  }
  if (status === 'missed') {
    // return <Image src={Available} alt="missed" sx={{ width: 45, height: 32, filter: 'grayscale(100%)' }} />;
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center' }}>
        <Image
          src={Available}
          alt="available"
          sx={{ width: 45, height: 32, filter: 'grayscale(100%)', transform: 'rotate(15deg)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 24,
            height: 6,
            borderRadius: '50%',
            background: 'rgba(242, 140, 0, 0.45)',
            filter: 'blur(3px)',
          }}
        />
      </Box>
    );
  }
  // return <Image src={Available} alt="available" sx={{ width: 45, height: 32, }} />;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', justifyContent: 'center' }}>
      <Image src={Available} alt="available" sx={{ width: 45, height: 32, transform: 'rotate(15deg)' }} />
      <Box
        sx={{
          position: 'absolute',
          bottom: -2,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 24,
          height: 6,
          borderRadius: '50%',
          background: 'rgba(242, 140, 0, 0.45)',
          filter: 'blur(3px)',
        }}
      />
    </Box>
  );
};

const getDayBgColor = (isClaimed, isMissed) => {
  if (isClaimed) return '#4caf50';
  if (isMissed) return '#9e9e9e';
  return 'transparent';
};

const getDayLabelColor = (isToday) => {
  if (isToday) return 'text.secondary';
  return 'text.secondary';
};

// =====================
// Default Days (status = upcoming/missed/today/claimed)
// =====================
const DEFAULT_DAYS = dailyCheckInWeekRewards.map((x, idx) => ({ day: idx + 1, points: x, status: 'upcoming' }));

// =====================
// Day Item Component
// =====================
const DayItem = ({ day, points, status }) => {
  const theme = useTheme();
  const { translate } = useLocales();
  const isToday = status === 'today';
  const isClaimed = status === 'claimed';
  const isMissed = status === 'missed';
  if (isMissed) day = 'Missed';
  else day = `${translate('day')} ${day}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        p: 1,
        borderRadius: 1,
        border: isToday ? `1px solid ${theme.palette.primary.main}` : '1px solid #f2f2f2',
        // bgcolor: isToday ? 'rgba(242,197,86,0.1)' : 'transparent',
        // eslint-disable-next-line no-nested-ternary
        background: isToday
          ? isSafariOrIOS
            ? theme.palette.gradients.theme3Ios
            : theme.palette.gradients.theme3
          : 'linear-gradient(180deg, #F1F1F1 0%, #FFFFFF 65%)',
        minWidth: 52,
      }}
    >
      {/* Points */}
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#000' }}>+{points}</Typography>

      {/* Icon Box */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          // bgcolor: getDayBgColor(isClaimed, isMissed),
        }}
      >
        {getDayIcon(status)}
      </Box>

      {/* Day Label */}
      <Typography
        sx={{
          fontSize: '0.75rem',
          color: getDayLabelColor(isToday),
          fontWeight: isToday ? 700 : 400,
        }}
      >
        {isToday ? `${translate('today')}` : day}
      </Typography>
    </Box>
  );
};

// =====================
// Main Component
// =====================
const DailyCheckin = ({ open, onClose }) => {
  const { translate } = useLocales();
  const [isClaiming, setIsClaiming] = useState(false);
  const dispatch = useDispatch();
  const { dailyCheckIn } = useSelector((x) => x.lookup);
  const theme = useTheme();

  const totalEarn = dailyCheckIn?.totalRewardPointEarn || 0;
  const history = dailyCheckIn?.checkInTransaction || [];
  const isClaimed = dailyCheckIn?.dailyCheckInStatus;

  const { enqueueSnackbar } = useSnackbar();

  const title = 'daily_checkin';
  const subtitle = 'Double reward for continuous check-in for 7 days';

  const days = useMemo(() => {
    const checkInStreak = Number(dailyCheckIn?.checkInStreak || 0);

    return DEFAULT_DAYS.map((x) => {
      const newData = { ...x };
      if (checkInStreak < x.day) newData.status = 'upcoming';
      if (checkInStreak >= x.day) newData.status = 'claimed';
      if (checkInStreak + 1 === x.day && isClaimed === false) newData.status = 'today';

      return newData;
    });
  }, [dailyCheckIn?.checkInStreak, isClaimed]);

  const onClaim = async () => {
    try {
      setIsClaiming(true);

      const response = await conObj.post('v1/user/daily-check-in');

      console.log(response);

      if (response?.data?.success) {
        enqueueSnackbar(response?.data?.message);

        const newDailyCheckin = { ...dailyCheckIn };

        newDailyCheckin.checkInStreak = response?.data?.check_in_streak;
        newDailyCheckin.dailyCheckInStatus = true;

        dispatch(setDailyCheckin(newDailyCheckin));
        getUserMemberBalance();
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(error?.response?.data?.message || error?.message, { variant: 'error' });
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={translate(title)}
      isBackdropClosable
      fullWidth={false} // disable fullWidth
      paperPropsSx={{
        background: isSafariOrIOS ? theme.palette.gradients.theme3Ios : theme.palette.gradients.theme3,
        maxWidth: '480px',
        width: '100%',
        overflow: 'visible', // allow coin to overflow outside modal
      }}
    >
      <Box sx={{ p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Total Earn + Coin - overlapping section */}
        <Box sx={{ position: 'relative', mb: 0, mt: 3 }}>
          {/* Total Earn Text */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.95rem', color: '#000', fontWeight: 600 }}>
              {translate('total_earn')}
            </Typography>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, color: '#202020' }}>
              {totalEarn.toLocaleString()}
            </Typography>
          </Box>

          {/* Coin Image - positioned top right, overlapping */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: 40,
              width: '160px',
              height: '160px',
              zIndex: 1,
            }}
          >
            <Image src={Coin} alt="Coin Icon" sx={{ width: 180, height: 210, objectFit: 'contain' }} />
          </Box>
        </Box>

        {/* Daily Check-in Box - coin overlaps on top right */}
        <Box
          sx={{
            // bgcolor: '#fff',
            borderRadius: 3,
            p: 2,
            // boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 2,
            position: 'relative',
            zIndex: 1,
            mt: 1,
            backgroundImage: `url(${DailyBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.3 }}>{translate('daily_checkin')}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#000', mb: 2 }}>
            {translate('double_reward_checkin')}{' '}
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.primary' }}>
              7{' '}
            </Typography>
            {translate('days')}
          </Typography>

          {/* Days Row */}
          <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 1 }}>
            {days.map((d, i) => (
              <DayItem key={i} {...d} />
            ))}
          </Box>

          {/* Claim Button */}
          <LoadingButton
            fullWidth
            variant="contained"
            loading={isClaiming}
            disabled={isClaimed || isClaiming}
            onClick={onClaim}
            sx={{
              borderRadius: 5,
              py: 1.5,
              mt: 2,
              background: isClaimed ? '#ccc' : theme.palette.gradients.buttonPrimary,
              border: `2px solid ${palette.header.register_btn.border}`,
              color: '#000',
              fontWeight: 700,
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': { background: theme.palette.gradients.theme2 },
              '&.Mui-disabled': { background: '#ccc', color: '#fff' },
            }}
          >
            {translate('claim_now')}
          </LoadingButton>
        </Box>

        {/* Check-in History - outside white box */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>{translate('check_in_history')}</Typography>

        <Box sx={{ maxHeight: 220, overflowY: 'auto', flex: 1 }}>
          {history.length > 0 ? (
            history.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>{item.date}</Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#4caf50' }}>
                  +{item.reward_point}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>
              {translate('no_history_yet')}
            </Typography>
          )}
        </Box>
      </Box>
    </CustomModal>
  );
};

export default DailyCheckin;
